import {
	useRef,
	useState,
	type CSSProperties,
	type FormEvent,
	type ReactElement,
} from "react";
import { useNavigate } from "@tanstack/react-router";
import { useMutation } from "@tanstack/react-query";
import { FiLoader } from "react-icons/fi";
import { supabase } from "@baseline/web/supabase";

interface UrlInputFormProps {
	sessionId?: string;
	className?: string;
	style?: CSSProperties;
	inputContainerClassName?: string;
	url?: string;
	onUrlChange?: (url: string) => void;
	inputRef?: React.RefObject<HTMLInputElement | null>;
}

export function UrlInputForm({
	sessionId,
	className,
	style,
	inputContainerClassName = "group relative w-full",
	url: controlledUrl,
	onUrlChange,
	inputRef: externalRef,
}: UrlInputFormProps): ReactElement {
	const navigate = useNavigate();
	const internalRef = useRef<HTMLInputElement>(null);
	const ref = externalRef ?? internalRef;
	const [internalUrl, setInternalUrl] = useState("");

	const isControlled = controlledUrl !== undefined;
	const url = isControlled ? controlledUrl : internalUrl;

	const submitUrl = useMutation({
		mutationFn: async (inputUrl: string) => {
			const body: Record<string, unknown> = { url: inputUrl };
			if (sessionId) body.sessionId = sessionId;

			const { data, error } = await supabase.functions.invoke(
				"process-url",
				{ body },
			);

			if (error) {
				throw new Error(error.message || "Failed to process URL");
			}

			return data as { contentId: string };
		},
		onSuccess: (data) => {
			navigate({
				to: "/content/$contentId",
				params: { contentId: data.contentId },
				search: { highlight: undefined },
			});
		},
	});

	function handleSubmit(e: FormEvent): void {
		e.preventDefault();
		const trimmedUrl = url.trim();
		if (trimmedUrl) {
			submitUrl.mutate(trimmedUrl);
		}
	}

	function handleChange(value: string): void {
		if (!isControlled) setInternalUrl(value);
		onUrlChange?.(value);
	}

	return (
		<form onSubmit={handleSubmit} className={className} style={style}>
			<div className={inputContainerClassName}>
				<input
					ref={ref}
					type="url"
					value={url}
					onChange={(e) => handleChange(e.target.value)}
					disabled={submitUrl.isPending}
					className="border-line hover:border-accent-subtle bg-surface-raised text-fg-strong placeholder-fg-ghost focus:border-accent w-full border py-5 pr-52 pl-6 text-base font-light transition-all duration-300 focus:bg-white focus:ring-0 focus:outline-none"
					placeholder="Paste a URL..."
					data-testid="url-input"
				/>
				<button
					type="submit"
					disabled={!url.trim() || submitUrl.isPending}
					className="bg-accent text-surface hover:bg-accent-dim absolute top-2 right-2 bottom-2 px-8 text-[11px] font-bold tracking-widest uppercase transition-colors disabled:pointer-events-none disabled:opacity-50"
					data-testid="extract-button"
				>
					{submitUrl.isPending ? (
						<span className="flex items-center gap-2">
							<FiLoader className="animate-spin" />
							Processing
						</span>
					) : (
						"Extract"
					)}
				</button>
			</div>
			{submitUrl.isError && (
				<p className="mt-2 text-sm text-red-400">
					{submitUrl.error.message}
				</p>
			)}
		</form>
	);
}
